/**
 * Hand-curated English → Tamil dictionary.
 *
 * These mappings are applied BEFORE phonetic transliteration in
 * `tanglishRuntime.js`. The longest-key-first match order guarantees
 * multi-word phrases like "Number of Guests" win over single-word
 * matches like "Number".
 *
 * Keys are case-insensitive. Match boundaries are whole-word for keys
 * that are single words, and exact-phrase for keys that contain spaces.
 * Add new entries here at any time — the runtime picks them up on the
 * next render.
 */
export const WORD_TA = {
  // --- Roles & People ---
  'photographer': 'போட்டோகிராபர்',
  'videographer': 'வீடியோகிராபர்',
  'caterer': 'கேட்டரர்',
  'caterers': 'கேட்டரர்கள்',
  'decorator': 'டெக்கரேட்டர்',
  'decorators': 'டெக்கரேட்டர்கள்',
  'venue provider': 'மண்டபம் மற்றும் மஹால்',
  'captain': 'கேப்டன்',
  'anchor / emcee': 'ஆங்கர் / எம்சி',
  'anchor': 'ஆங்கர்',
  'emcee': 'எம்சி',
  'mehendi artist': 'மெஹந்தி ஆர்டிஸ்ட்',
  'bridal makeup artist': 'பிரைடல் மேக்கப் ஆர்டிஸ்ட்',
  'makeup artist': 'மேக்கப் ஆர்டிஸ்ட்',
  'bartender': 'பார்டெண்டர்',
  'waiter': 'வெயிட்டர்',
  'chef': 'செஃப்',
  'dj': 'டி ஜே',

  // --- Services & Segments ---
  'photography': 'போட்டோகிராபி',
  'videography': 'வீடியோகிராபி',
  'catering': 'கேட்டரிங்',
  'decoration': 'டெக்கரேஷன்',
  'cake': 'கேக்',
  'flowers': 'பிளவர்ஸ்',
  'music': 'மியூசிக்',
  'entertainment': 'என்டர்டெயின்மென்ட்',
  'bouncer': 'பவுன்சர்',
  'live counter': 'லைவ் கவுண்டர்',
  'buffet': 'பஃபெ',
  'banquet hall': 'பாங்குவெட் ஹால்',
  'beach / outdoor': 'பீச் / அவுட்டோர்',
  'stage decoration': 'ஸ்டேஜ் டெக்கரேஷன்',
  'balloon decoration': 'பலூன் டெக்கரேஷன்',

  // --- Event Types ---
  'beach wedding': 'பீச் வெட்டிங்',
  'sangeet': 'சங்கீத்',
  'mehendi': 'மெஹந்தி',
  'naming ceremony': 'நேமிங் செரிமோனி',
  'welcome': 'வெல்கம்',

  // --- Page Headings / Actions ---
  'tell us about your event and get estimated costs instantly':
    'உங்கள் நிகழ்வைப் பற்றி எங்களிடம் கூறுங்கள், உத்தேச செலவுகளை உடனடியாகப் பெறுங்கள்',
  'pick your city': 'உங்கள் ஊர்',
  'use my location': 'யூஸ் மை லொக்கேஷன்',
  'event location': 'ஈவென்ட் லொக்கேஷன்',
  'number of guests': 'விருந்தினர் எண்ணிக்கை',
  'add to cart': 'சேர்க்க',
  'remove from cart': 'ரிமூவ் ஃப்ரம் கார்ட்',
  'view package': 'பேக்கேஜ் பார்க்க',
  'book now': 'முன்பதிவு செய்யுங்கள்',
  'confirm': 'கன்ஃபர்ம்',
  'pay now': 'பணம் செலுத்து',
  'refresh': 'ரிஃப்ரெஷ்',
  'search': 'தேடு',
  'filter': 'ஃபில்டர்',
  'sort by': 'சோர்ட் பை',
  'settings': 'செட்டிங்ஸ்',

  // Single-word fallbacks (used when a label like "Event Location" is
  // split across multiple text nodes / spans). Order doesn't matter here
  // because they're shorter than their phrasal counterparts above and
  // the regex sorts by length descending.
  'event': 'ஈவென்ட்',
  'location': 'லொக்கேஷன்',
  'guests': 'விருந்தினர்',
  'guest': 'விருந்தினர்',
  'city': 'ஊர்',
  'cart': 'கார்ட்',
  'package': 'பேக்கேஜ்',
  'book': 'பதிவு',
  'pay': 'பணம்',
  'add': 'சேர்',
  'view': 'பார்',
  'remove': 'நீக்கு',
  'cancel': 'ரத்து',
  'wedding': 'திருமணம்',
  'birthday': 'பிறந்தநாள்',
  'balloon': 'பலூன்',
  'flower': 'பூ',
  'gift': 'பரிசு',
  'stage': 'ஸ்டேஜ்',
  'beach': 'பீச்',
  'outdoor': 'அவுட்டோர்',
  'price': 'விலை',
  'join': 'இணைக்க',

  // --- Status / Labels ---
  'available': 'அவைலபிள்',
  'unavailable': 'கிடைக்கவில்லை',
  'pending': 'நிலுவையில் உள்ளது',
  'confirmed': 'உறுதி',
  'cancelled': 'கேன்சல்டு',
  'completed': 'கம்ப்ளீட்டட்',
  'approved': 'அங்கீகரிக்கப்பட்டது',
  'rejected': 'நிராகரிக்கப்பட்டது',
  'inactive': 'ஆக்டிவ் ஆகல',     // placed BEFORE 'active' so longer match wins
  'active': 'ஆக்டிவ்',

  // --- Common phrases ---
  'coming soon': 'கம்மிங் சூன்',
  'no results': 'நோ ரிசல்ட்ஸ்',
  'try again': 'ட்ரை அகெயின்',
  'read more': 'ரீட் மோர்',
  'see all': 'சீ ஆல்',

  // --- User-provided exact mappings (Feb 2026) ---
  'team': 'டீம்',
  'biriyani specialist': 'பிரியாணி நிபுணர்',
  'menus & pricing': 'மெனுஸ் & ப்ரைசிங்',
  'scan menu photo with ai': 'ஸ்கேன் மெனு போட்டோ வித் ஏஐ',
  'tiffin': 'டிபன்',
  'lunch': 'லஞ்ச்',
  'snacks': 'ஸ்நாக்ஸ்',
  'dinner': 'டின்னர்',
  'save menus': 'சேவ் மெனுஸ்',
  'service price': 'சர்வீஸ் பிரைஸ்',
  // Role / form labels — Tanglish preference
  'customer': 'கஸ்டமர்',
  'vendor': 'வெண்டர்',
  'full name': 'ஃபுல் நேம்',
  'phone number': 'போன் நம்பர்',
};

/** Build a regex matching any dictionary key, longest first. Whole-word
 *  boundaries for single-word keys, exact substring (case-insensitive)
 *  for multi-word phrases. */
const escape = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const sortedKeys = Object.keys(WORD_TA).sort((a, b) => b.length - a.length);

export const WORD_TA_RX = new RegExp(
  '(?:' + sortedKeys.map((k) => {
    // For multi-word phrases we use simple boundaries (whitespace/start/end
    // or punctuation). For single-word keys we use \b on both sides.
    if (k.includes(' ')) {
      return '(?<=^|[\\s.,;:!?"\\(\\)\\-\\u2013\\u2014])' + escape(k) + '(?=$|[\\s.,;:!?"\\(\\)\\-\\u2013\\u2014])';
    }
    return '\\b' + escape(k) + '\\b';
  }).join('|') + ')',
  'gi',
);

export default WORD_TA;
